import { useEffect, useMemo, useRef } from 'react';

import type { SchemaChatResolver_GetMessages } from 'cpool-tables/backend-resolvers';

import { DateSeparator } from './DateSeparator';
import { MessageBubble } from './MessageBubble';
import Spinner from '~/components/icons/Spinner';
import type { ChatMessage, LabeledMessage } from '~/features/whatsapp/types/message';
import { dateTimeTwoDigitFormat } from '~/lib/i18n';

type Props = {
    chatId?: string;
    hasOlderMessages?: boolean;
    isLoadingOlder?: boolean;
    labeledMessages?: Array<LabeledMessage>;
    messages?: SchemaChatResolver_GetMessages;
    onLoadOlder?: () => void;
};

export const MessageStream = ({
    chatId,
    hasOlderMessages = false,
    isLoadingOlder = false,
    labeledMessages,
    messages,
    onLoadOlder,
}: Props) => {
    const bottomRef = useRef<HTMLDivElement>(null);
    const topSentinelRef = useRef<HTMLDivElement>(null);
    const items: Array<LabeledMessage> = useMemo(
        () => labeledMessages ?? messages?.map((message) => ({ message, senderLabel: undefined })) ?? [],
        [labeledMessages, messages],
    );
    const newestMessageId = items[items.length - 1]?.message.messageId;

    // Reply stores only the WAMID; unresolved (original older than the fetched window) renders without a quote.
    const byMetaMessageId = useMemo(
        () =>
            new Map<string, ChatMessage>(
                items
                    .map(({ message }) => message)
                    .filter((message) => message.metaMessageId)
                    .map((message) => [message.metaMessageId as string, message]),
            ),
        [items],
    );

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatId, newestMessageId]);

    useEffect(() => {
        if (!onLoadOlder || !hasOlderMessages) return undefined;

        const sentinel = topSentinelRef.current;
        if (!sentinel) return undefined;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting && !isLoadingOlder) onLoadOlder();
        });
        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [hasOlderMessages, isLoadingOlder, onLoadOlder]);

    let lastDay: string | null = null;

    return (
        <div className="flex flex-col py-2">
            <div ref={topSentinelRef} className="h-px shrink-0" />
            {isLoadingOlder && (
                <div className="flex items-center justify-center py-2">
                    <Spinner className="h-4 w-4" />
                </div>
            )}
            {items.map(({ message, senderLabel }) => {
                const day = dateTimeTwoDigitFormat(new Date(message.timestamp));
                const showSeparator = day !== lastDay;
                lastDay = day;

                return (
                    <div key={message.messageId}>
                        {showSeparator && <DateSeparator label={day} />}
                        <MessageBubble
                            message={message}
                            quotedMessage={
                                message.repliedToMetaMessageId
                                    ? byMetaMessageId.get(message.repliedToMetaMessageId)
                                    : undefined
                            }
                            senderLabel={senderLabel}
                        />
                    </div>
                );
            })}
            <div ref={bottomRef} />
        </div>
    );
};
